import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { AcademyVideo } from '../../academy/academy-catalog';
import { HINT_FILMS } from '../portal-hints/hint-films';
import { HintAnimation } from '../portal-hints/hint-motion';
import { ORBI_IDLE } from '../orbi-lab/orbi-lab.component';
import { FilmRef } from './films/lesson-films';

const NO_SCENES: FilmRef[] = [];

/** Orbi im Platzhalter: schaut, kichert, ruht. */
const PLACEHOLDER_SCRIPT = [ORBI_IDLE, 'giggle-bounce', ORBI_IDLE, ORBI_IDLE];

/**
 * Video einer Academy-Lektion. Drei Quellen (`AcademyVideo`):
 *  - `hint`: der Erklärfilm des verknüpften Portalhinweises (`app-hint-film`,
 *    Loop) — derselbe Film wie in der Hinweis-Box;
 *  - `scenes`: Lektionsfilm aus Szenen (`app-academy-film`), einmal abgespielt;
 *  - `file`: Videodatei mit Steuerung (z. B. später KI-generiert);
 *  - `placeholder`: Orbi und „Video folgt“.
 * Die Breite folgt dem Container (ResizeObserver); der Hinweis-Film skaliert
 * seine Bühne darauf.
 */
@Component({
  selector: 'app-academy-video',
  templateUrl: './academy-video.component.html',
  styleUrls: ['./academy-video.component.scss'],
})
export class AcademyVideoComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) video!: AcademyVideo;
  /** Beschriftung für Screenreader (Titel der Lektion). */
  @Input() label = '';
  /** Länge in Sekunden — steht unter dem Video. */
  @Input() seconds?: number;
  /** Untertitel je Szene (nur `kind: 'scenes'`). */
  @Input() captions: string[] = [];

  film: HintAnimation | null = null;
  width = 0;
  readonly placeholderScript = PLACEHOLDER_SCRIPT;

  private observer?: ResizeObserver;

  constructor(
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['video']) {
      this.film = this.video?.kind === 'hint' ? HINT_FILMS[this.video.hintId]() : null;
    }
  }

  ngAfterViewInit(): void {
    this.measure();
    if (typeof ResizeObserver === 'function') {
      this.observer = new ResizeObserver(() => this.zone.run(() => this.measure()));
      this.observer.observe(this.host.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  get duration(): string {
    const s = Math.max(0, Math.round(this.seconds ?? 0));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  get fileVideo(): { src: string; poster?: string } | null {
    return this.video?.kind === 'file' ? this.video : null;
  }

  get sceneRefs(): FilmRef[] {
    return this.video?.kind === 'scenes' ? this.video.scenes : NO_SCENES;
  }

  private measure(): void {
    const w = Math.floor(this.host.nativeElement.clientWidth);
    if (w > 0 && w !== this.width) this.width = w;
  }
}
